import { formatRecipeDiscount } from 'utils/format'

// The discount's source in the order API: Core (promotions_v1) or an offer (promotions_v2)
type ItemisedDiscount = {
  source: 'promotions_v1' | 'promotions_v2'
  amount: string
  percentage?: string
  promotionId?: string
}

export type DiscountLine = {
  key: string
  kind: 'offer' | 'percentage' | 'total'
  label: string
  amount: string
  // Only on Core discounts, for pages that word the label themselves
  percentage?: string
}

type GetDiscountLinesParams = {
  discounts?: ItemisedDiscount[] | null
  totalDiscount?: string | number | null
  // The single recipe discount line would be shown: there is a recipe discount, or it is always shown
  canShowRecipeDiscountLine: boolean
}

type DiscountLines = {
  lines: DiscountLine[]
  showRecipeDiscountLine: boolean
}

const OFFER_SOURCE = 'promotions_v2'

const isPositive = (value?: string | number | null) => Number(value) > 0

const toLabel = ({ source, percentage }: ItemisedDiscount) =>
  source === OFFER_SOURCE
    ? 'Offer savings'
    : formatRecipeDiscount(percentage ? Number(percentage) : undefined)

// Core discounts first, then offers, each keeping the order given
const coreDiscountsFirst = (discounts: ItemisedDiscount[]) => [
  ...discounts.filter(({ source }) => source !== OFFER_SOURCE),
  ...discounts.filter(({ source }) => source === OFFER_SOURCE),
]

const toItemisedLines = (discounts: ItemisedDiscount[]): DiscountLine[] =>
  coreDiscountsFirst(discounts.filter(({ amount }) => isPositive(amount))).map(
    (discount, index) => ({
      key: `${discount.source}-${discount.promotionId ?? index}`,
      kind: discount.source === OFFER_SOURCE ? 'offer' : 'percentage',
      label: toLabel(discount),
      amount: discount.amount,
      ...(discount.source !== OFFER_SOURCE && discount.percentage
        ? { percentage: discount.percentage }
        : {}),
    }),
  )

const toTotalLine = (totalDiscount: GetDiscountLinesParams['totalDiscount']): DiscountLine[] =>
  isPositive(totalDiscount)
    ? [
        {
          key: 'total',
          kind: 'total',
          label: formatRecipeDiscount(),
          amount: String(totalDiscount),
        },
      ]
    : []

/*
 * Which discount lines the receipt shows. In order of precedence, so nothing is counted twice:
 * 1. the itemised discounts, when the order has any
 * 2. the single recipe discount line, rendered by the receipt as it always has been
 * 3. one line for the total discount (older orders have it but no itemised discounts)
 */
export const getDiscountLines = ({
  discounts,
  totalDiscount,
  canShowRecipeDiscountLine,
}: GetDiscountLinesParams): DiscountLines => {
  const lines = toItemisedLines(discounts ?? [])

  if (lines.length > 0) return { lines, showRecipeDiscountLine: false }
  if (canShowRecipeDiscountLine) return { lines: [], showRecipeDiscountLine: true }

  return { lines: toTotalLine(totalDiscount), showRecipeDiscountLine: false }
}
